import React from 'react';
import { Link } from 'react-router-dom';

/**
 * The user manual rendered on /help as structured JSX sections.
 *
 * Each section has:
 *   id         — URL hash + testid suffix
 *   title      — displayed heading + TOC entry
 *   body       — JSX content
 *   searchText — flat text for the sidebar search filter
 *
 * This is the on-screen companion to /app/USER_MANUAL.md. Keep both in
 * sync when adding features so docs and in-app help don't drift.
 */

const P = (props) => <p className="text-slate-700 leading-relaxed" {...props} />;
const UL = (props) => <ul className="list-disc pl-6 space-y-1.5 text-slate-700" {...props} />;
const OL = (props) => <ol className="list-decimal pl-6 space-y-1.5 text-slate-700" {...props} />;
const H3 = (props) => <h3 className="text-lg font-bold text-slate-900 mt-4 mb-2" {...props} />;
const Code = ({ children }) => (
  <code className="bg-slate-100 text-slate-800 px-1.5 py-0.5 rounded text-sm">{children}</code>
);
const B = ({ children }) => <strong className="text-slate-900 font-semibold">{children}</strong>;

export const MANUAL_SECTIONS = [
  {
    id: 'getting-started',
    title: '1. Getting Started',
    searchText:
      'sign up sign in signup login google verification email guest claim welcome tour dashboard my schedules duplicate archive rename delete recently deleted restore settings organization logo accent color install pwa app',
    body: (
      <>
        <H3>Signing up & signing in</H3>
        <UL>
          <li>
            <B>Sign up</B> at <Code>/login</Code> with an email + password, or use{' '}
            <B>Continue with Google</B>.
          </li>
          <li>A verification email is sent on signup — click the link to confirm.</li>
          <li>
            Forgot password? Use <Code>/forgot-password</Code> to receive a reset link.
          </li>
          <li>
            No account yet? You can build a Round Robin schedule straight away. When you sign up,
            the schedule is saved to your new account and a short welcome tour shows you around.
          </li>
        </UL>
        <H3>My Schedules</H3>
        <P>
          After signing in you land on <B>My Schedules</B>. Every schedule you own or
          collaborate on appears as a card — click it to open. Card buttons:{' '}
          <B>Duplicate</B>, <B>Archive</B> and <B>Delete</B>, plus <B>Rename</B> (Pro). Pro
          users can also <B>Select</B> several schedules to delete them together. Archived
          schedules are kept under <B>Archived</B>.
        </P>
        <P>
          Deleted schedules (and deleted stats games and seasons) go to{' '}
          <B>Recently deleted</B> for 7 days, where you can <B>Restore</B> them.
        </P>
        <P>
          Click <B>Create New</B> to launch the Event Wizard, or <B>Import Schedule</B> to
          upload an existing one as a PDF, CSV, or Excel file (Pro).
        </P>
        <H3>Settings</H3>
        <P>
          User menu → <B>Settings</B>: full name, organization name and logo, brand accent
          colour (Pro), email notification preferences, change password, and{' '}
          <B>Sign out of this device</B>.
        </P>
        <H3>Install as an app (PWA)</H3>
        <P>
          On any device, click the <B>Install App</B> button in the header to add Schedule
          Guru to your home screen or dock. It auto-updates when we ship new features — an{' '}
          <B>Update Available</B> banner appears the moment a new version is ready. There is
          no separate App Store or Google Play app.
        </P>
      </>
    ),
  },
  {
    id: 'creating-schedule',
    title: '2. Creating a Schedule',
    searchText:
      'event wizard create schedule event info sport timezone venue courts fields game days blackout teams bulk paste csv pools format round robin double round robin single elimination double elimination hybrid bracket tournament rules games per day duration buffer rest back-to-back home away playoffs seeding consolation import pdf csv excel',
    body: (
      <>
        <H3>The Event Wizard</H3>
        <P>
          <Code>/create</Code> opens a step-by-step wizard:
        </P>
        <OL>
          <li>
            <B>Event Info</B> — event name, sport, start date, timezone, venue(s), number of
            courts/fields per game day, game days and optional blackout dates. Sports: Basketball,
            Soccer, Volleyball, Netball, Pickleball, Flag Football, Softball, Baseball, Cricket,
            or Generic (score only).
          </li>
          <li>
            <B>Teams</B> — type team names, paste a list (one per line), or import a CSV
            (a template is provided). The order you enter is the seed order.
          </li>
          <li>
            <B>Pools</B> (shown for 4+ teams, Pro) — turn pools on, choose how many, and assign
            each team to a pool.
          </li>
          <li>
            <B>Format</B> — Round Robin, Double Round Robin, Single Elimination, Double
            Elimination, Hybrid (Round Robin + Playoffs) or <B>Bracket Tournament</B>.
          </li>
          <li>
            <B>Rules</B> — games per day (per venue), game duration, buffer time, minimum rest
            time, no back-to-back games, home/away designation, and the first game start time
            (the game times are generated for you).
          </li>
          <li>
            <B>Playoffs</B> (optional, round robin formats) — teams to advance, single or double
            elimination, seeding method (win/loss record, head-to-head, point differential or
            manual) and an optional consolation bracket.
          </li>
          <li>
            <B>Review</B> → create. The event opens in My Schedules.
          </li>
        </OL>
        <H3>Who can use which format</H3>
        <UL>
          <li>Round Robin is free for everyone, even without an account.</li>
          <li>Every other format needs a free account.</li>
          <li>Free accounts are limited to 8 teams per event and can't use pools.</li>
          <li>Pro removes the team limit and unlocks pools.</li>
        </UL>
        <H3>Bracket Tournament</H3>
        <P>
          Pick <B>Bracket Tournament</B> at the Format step, choose a bracket that fits your team
          count, check the <B>Preview</B>, set <B>Seeds</B> and rearrange <B>Matchups</B> or byes.
          You can also start from the Bracket Library with <B>Manage as Tournament</B> (Pro). See{' '}
          <B>Brackets</B> below for the details.
        </P>
        <H3>Importing an existing schedule (Pro)</H3>
        <P>
          <Code>/import</Code> accepts PDF, CSV, or Excel. Pick the sport, and the AI reads the
          file, extracts teams/dates/times/venues/matchups, and shows a confirmation screen
          where you can adjust everything before saving.
        </P>
      </>
    ),
  },
  {
    id: 'schedule-view',
    title: '3. The Schedule View',
    searchText:
      'schedule view table calendar standings logos teams leaderboard season stats filters score forfeit volleyball sets date time venue drag reorder add match delete bulk delete assign teams lock regenerate venues visibility hidden share dashboard tie-breakers scoring rules points collaborators editor viewer exports pdf excel ical print white label',
    body: (
      <>
        <P>
          Opens at <Code>/schedule/:eventId</Code>. The header has <B>Dashboard</B>{' '}
          (tournament progress), <B>Officials</B>, <B>Bracket</B> (elimination formats),{' '}
          <B>Share</B>, <B>Visible / Hidden</B>, <B>Notify</B> (Pro) and <B>Export</B>.
        </P>
        <H3>Views</H3>
        <UL>
          <li><B>Table</B> — every match, filterable by day, venue/court and team.</li>
          <li><B>Calendar</B> — matches by month.</li>
          <li><B>Standings</B> (Pro) — live table plus <B>Scoring Rules</B>.</li>
          <li>
            <B>Logos / Teams</B> (Pro) — event and team logos, team names, collaborators, team
            contacts and rosters.
          </li>
          <li>
            <B>Leaderboard</B> and <B>Season Stats</B> — player stats for every sport except
            basketball. Basketball leagues get a <B>Stats</B> button (Pro) that opens the Stats Hub.
          </li>
        </UL>
        <H3>Entering scores</H3>
        <UL>
          <li>
            Click the pencil in a match&apos;s <B>Score</B> column, type both scores and save. Any
            signed-in organizer can enter scores, on Free or Pro.
          </li>
          <li>Volleyball takes a score for every set (up to 5).</li>
          <li>
            Cricket takes runs / wickets for each team, plus overs if you have them (e.g. 18.4). The
            score shows as 192/4 (18.4 Overs).
          </li>
          <li>You can also mark a forfeit (Team A, Team B or both).</li>
          <li>Standings update right away, and bracket winners advance on their own.</li>
        </UL>
        <H3>Editing matches (Pro)</H3>
        <UL>
          <li>Click a date/time to change it, or pick a different venue.</li>
          <li>Turn on drag mode to reorder matches.</li>
          <li>Add a match, delete one, or tick several and delete them together.</li>
          <li>Assign the teams for later bracket rounds by hand.</li>
          <li>Print blank stat sheets for every game you can see.</li>
        </UL>
        <P>
          Every match also has a <B>Game Stats</B> link, a <B>Print blank stat sheet</B> button
          and a <B>Lock</B> padlock.
        </P>
        <H3>Regenerate</H3>
        <P>
          Click <B>Regenerate</B> in the toolbar. The schedule rebuilds with a fresh
          arrangement (locked matches stay). <B>Every click produces a different
          schedule</B> — hit it until you find one you like.
        </P>
        <H3>Venues</H3>
        <P>
          The <B>Venues</B> button (or <B>Add Venue</B> if there are none yet) adds, renames and
          removes venues.
        </P>
        <H3>Visibility</H3>
        <P>
          Flip <B>Visible / Hidden</B> in the header. Hidden schedules show &quot;not found&quot;
          on every public page, in one click.
        </P>
        <H3>Standings & tie-breakers (Pro)</H3>
        <P>
          Teams are ranked by points first, then by each sport&apos;s tie-breakers. Any teams
          still level are listed alphabetically.
        </P>
        <UL>
          <li>
            <B>Basketball, Netball, Generic</B>: head-to-head wins → head-to-head point
            (goal) difference → overall point (goal) difference
          </li>
          <li>
            <B>Soccer</B>: goal difference → goals scored → head-to-head
          </li>
          <li>
            <B>Volleyball / Pickleball</B>: match wins → set difference → sets won → head-to-head
          </li>
          <li>
            <B>Flag Football / Softball / Baseball</B>: head-to-head → point (run)
            difference → points (runs) scored
          </li>
          <li>
            <B>Cricket</B>: Net Run Rate (NRR) → head-to-head → run difference → runs scored.
            NRR is runs scored per over faced minus runs conceded per over bowled, from games where
            both teams&apos; overs were entered.
          </li>
        </UL>
        <P>
          Default points per sport (win / draw / loss): Basketball 2 / 0 / 1, Soccer 3 / 1 / 0,
          Volleyball 1 / 0 / 0, Pickleball 2 / 0 / 0, Netball, Flag Football, Softball, Baseball
          and Cricket 2 / 1 / 0, Generic 1 / 0 / 0. Change them (and forfeit points) under{' '}
          <B>Scoring Rules</B>.
        </P>
        <H3>Collaborators (Pro)</H3>
        <P>
          Open the <B>Logos / Teams</B> tab → <B>Collaborators</B> and invite another organizer
          by email as an <B>Editor</B> (full edit access except billing and delete) or a{' '}
          <B>Viewer</B> (read only). People who already have an account are added straight
          away; anyone else gets an email inviting them to create a free account.
        </P>
        <H3>Exports</H3>
        <P>
          <B>Export</B> opens the export page:
        </P>
        <UL>
          <li>PDF in portrait or landscape (saved from the print dialog).</li>
          <li>Excel.</li>
          <li>iCal (one calendar event per match).</li>
          <li>Print.</li>
        </UL>
        <P>
          Pro exports can carry your own branding. <B>Share</B> copies the public link or posts it
          to Facebook, X or WhatsApp.
        </P>
      </>
    ),
  },
  {
    id: 'teams-rosters',
    title: '4. Teams & Rosters',
    searchText:
      'teams rosters players jersey captain player photo team photo paste csv export pdf merge duplicates team contact invite link roster submitted lock rosters after games reminder promote on social promo image',
    body: (
      <>
        <H3>Managing teams (Pro)</H3>
        <P>
          Open the <B>Logos / Teams</B> tab of a schedule to rename teams, upload team and
          event logos, and manage each team&apos;s contact and roster.
        </P>
        <H3>Rosters</H3>
        <P>
          Click <B>Edit Roster</B> on a team to open the editor:
        </P>
        <UL>
          <li>Add players by jersey number + name, and add a player photo</li>
          <li>Mark a captain with the star button</li>
          <li>Upload a team photo</li>
          <li>
            Paste a roster from Excel or Google Sheets using <B>Paste Roster CSV</B> (it
            replaces the current rows)
          </li>
          <li>
            <B>Export</B> the roster as CSV (Excel/Sheets) or PDF (printable, branded with
            team + event, players jersey-sorted, captain starred)
          </li>
          <li><B>Merge duplicates</B> joins two records of the same player so their stats stay together</li>
        </UL>
        <H3>Team contact invitations</H3>
        <P>
          Give each team a contact name and email, then send the invite or copy the link. The
          contact opens their private team link and edits that team&apos;s roster without a
          Schedule Guru account. They can also export it. The panel shows how many teams have
          submitted their roster.
        </P>
        <H3>Locking rosters</H3>
        <P>
          Set <B>Lock rosters after</B> a number of games. Once that many games are played, team
          contacts see a read-only roster. Set it to 0 to never lock.
        </P>
        <H3>Reminders</H3>
        <P>
          Team contacts who haven&apos;t submitted a roster get an automatic reminder email the
          day before the event starts. You can also send a reminder yourself from the team list.
        </P>
        <H3>Promote on Social</H3>
        <P>
          <B>Promote on Social</B> makes a branded promo image for the event. Choose square
          (1080×1080) for posts or the wide link-preview size (1200×630), then download or share it.
        </P>
      </>
    ),
  },
  {
    id: 'live-stats',
    title: '5. Stats & Live Stats',
    searchText:
      'stats live stats basketball only box score soccer volleyball netball pickleball flag football softball baseball cricket stats hub new game setup link league match quarter length season clock lineup bench stat buttons play feed undo swipe long press edit play starters edit roster join code multi-device statistician synced clock end game summary csv share recap ai recap print stat sheet import pdf photo leaderboard season totals awards per rankings player of the week potw career card',
    body: (
      <>
        <H3>Which sports have stats</H3>
        <UL>
          <li>
            <B>Basketball</B> — the live stat tracker records every play during the game (Pro).
          </li>
          <li>
            <B>Soccer, Volleyball, Netball, Pickleball, Flag Football, Softball, Baseball and
            Cricket</B> — enter each player&apos;s box score after the game, or scan a paper
            stat sheet with AI (Pro). There is no live play-by-play tracker for these sports.
          </li>
          <li><B>Generic</B> — final score only.</li>
        </UL>
        <H3>Starting a live basketball game</H3>
        <P>
          Go to the <B>Stats Hub</B> (<Code>/stats</Code>) → <B>New Game</B>. Name the game,
          use <B>Link to League Match</B> so the stats attach to your schedule, load a saved team
          or paste the roster, pick the quarter length (8, 10, 12, 15 or 20 minutes; default 10)
          and an optional season. Already have the box score on paper? Use{' '}
          <B>Import Box Score</B> instead.
        </P>
        <H3>The game screen</H3>
        <UL>
          <li>Scoreboard with the game clock — tap to start or pause, tap the time to set it</li>
          <li>Quarter and overtime selector, and quick quarter lengths (8, 10, 12 minutes) in the menu</li>
          <li>
            Stat buttons: 2pt, 3pt and 1pt (free throw) made/miss, defensive and offensive
            rebound, assist, steal, block, turnover, foul
          </li>
          <li>Substitutions, a play feed and an <B>Undo</B> button</li>
          <li>When the clock hits 0:00 you choose: next quarter, overtime or end the game</li>
          <li>Optional game video link</li>
        </UL>
        <H3>Recording a stat</H3>
        <P>
          Tap the stat button, then tap the player (switch to the other team for stats like
          a steal against). The play lands in the feed with the game clock time.
        </P>
        <H3>Fixing a play</H3>
        <OL>
          <li>
            <B>Swipe left</B> on the top play — instant undo.
          </li>
          <li>
            <B>Long-press</B> the top play — instant undo.
          </li>
          <li>
            <B>Tap any recent play</B> — opens <B>Edit Play</B>. Change the player, team, or
            stat type, or delete the play entirely.
          </li>
        </OL>
        <H3>Starters</H3>
        <P>
          The lineup from the end of the previous period carries over. Change it any time
          from the menu (<B>Lineups</B>).
        </P>
        <H3>Edit a player mid-game</H3>
        <P>
          <B>Edit Roster</B> in the game menu renames a player or changes their jersey number
          without losing a single stat.
        </P>
        <H3>Multi-device stat tracking</H3>
        <P>
          Share the game&apos;s 6-character join code. Helpers enter it at <Code>/join</Code>{' '}
          (or <B>Join game</B> in the Stats Hub) and record stats from their own phone, with no
          account needed. The clock is kept on the server, so every device shows the same time
          and any of them can start or pause it. Your device shows a notice when a statistician
          joins.
        </P>
        <H3>End of game</H3>
        <P>
          <B>End Game</B> opens the game summary with the box score, team totals and shooting
          percentages. From there:
        </P>
        <UL>
          <li><B>Edit Stats</B> and <B>Analytics</B>.</li>
          <li>Export a <B>CSV</B>.</li>
          <li><B>Share Recap</B> for a share image.</li>
          <li><B>Generate Recap</B> for a two-paragraph AI write-up.</li>
        </UL>
        <H3>Box scores for other sports (Pro)</H3>
        <P>
          On the schedule, click the clipboard icon in a match&apos;s Score column. The roster is
          filled in — type each player&apos;s numbers and save, or click <B>Scan Box Score</B> to
          snap or upload a photo or PDF of each team&apos;s paper stat sheet (or one sheet with both
          teams); AI fills in the stats for you to check. In soccer, netball,
          softball and baseball, the players&apos; goals or runs make up the team score. Flag
          football can also <B>Track yards &amp; passing TDs</B>. The <B>Game Stats</B> page has an
          AI recap and a <B>Share Recap</B> image.
        </P>
        <H3>Printable stat sheets</H3>
        <P>
          Print a blank stat sheet for any game from the schedule (printer icon), laid out for
          the sport, with a legend for every abbreviation.
        </P>
        <H3>Stats Hub, seasons and awards (basketball)</H3>
        <UL>
          <li>The Stats Hub lists every game.</li>
          <li>Create <B>Seasons</B> (e.g. &quot;Spring 2026&quot;) to group games.</li>
          <li>See <B>Season Stats</B>, <B>Rankings</B> and <B>Awards</B> (efficiency / PER).</li>
          <li>Players get career stat sheets and shareable player cards.</li>
        </UL>
        <H3>Player of the Week graphics (Pro)</H3>
        <P>Make a shareable Player of the Week image from your league&apos;s stats:</P>
        <OL>
          <li>Open your league or tournament from <B>My Schedules</B>.</li>
          <li>Click <B>Stats</B> in the view tabs (pro feature).</li>
          <li>Click <B>Awards</B> at the top right.</li>
          <li>Open <B>Player of the Week</B> Tab.</li>
          <li>Pick the week, keep or swap the suggested player, choose <B>Square</B> or <B>Story</B>, then <B>Download</B>.</li>
        </OL>
        <P>
          Weeks run Monday to Sunday. The suggested player is the most efficient performer that
          week. Square is 1080×1080 for posts, Story is 1080×1920 for Instagram and Facebook
          stories. The Stats tab appears for basketball leagues and tournaments.
        </P>
      </>
    ),
  },
  {
    id: 'brackets',
    title: '6. Brackets',
    searchText:
      'bracket view print export pdf svg png layout classic mirrored letter tabloid landscape portrait auto optimize bracket library templates blank write-in customize share link team lists scores manage as tournament bracket tournament seeds matchups byes stats icons',
    body: (
      <>
        <H3>Bracket view</H3>
        <P>
          <Code>/bracket/:eventId</Code> shows the bracket for elimination events.
        </P>
        <UL>
          <li><B>Auto-optimize</B> picks the clearest layout and paper.</li>
          <li>
            <B>Options</B> switches between Classic and Mirrored layouts, Letter and Tabloid
            paper, landscape and portrait, and one or two pages.
          </li>
          <li>Zoom, export to PDF, SVG or PNG, or print.</li>
          <li>Click a game to enter its score; the winner advances.</li>
        </UL>
        <H3>Bracket Library</H3>
        <P>
          <Code>/bracket-library</Code> has free printable templates: Single and Double
          Elimination for 4–64 teams, plus an 8-team 3-Game Guarantee. Print or download any
          template with placeholders or as a blank write-in sheet.
        </P>
        <UL>
          <li>
            <B>Customize Bracket</B>: add a title, date, logo and team names. Free users see a
            Preview watermark; Pro prints and downloads it with no watermark.
          </li>
          <li>
            <B>Share link</B> (free account): a link that always shows the latest version.
          </li>
          <li><B>Team lists</B> (Pro): save a list of teams and fill any template with it.</li>
          <li><B>Scores on the bracket</B> (Pro): type scores and the winners advance.</li>
        </UL>
        <H3>Bracket Tournaments</H3>
        <P>
          A Bracket Tournament is a managed tournament in My Schedules. Every game has a date,
          time and court, and winners advance as scores come in. There are two ways to create one:
        </P>
        <OL>
          <li>
            <B>Wizard</B>: at the Format step pick <B>Bracket Tournament</B>. Single
            Elimination takes 2–64 teams, Double Elimination 3–64 teams (one grand final), and
            the 3-Game Guarantee exactly 8. Use the <B>Seeds</B> and <B>Matchups</B> tabs to
            arrange the draw. Seed 1 plays the lowest seed, and byes go to the top seeds.
          </li>
          <li>
            <B>Bracket Library</B> (Pro): customize a bracket, then click{' '}
            <B>Manage as Tournament</B>. The wizard opens already filled in, and any scores
            you typed can be imported as official results.
          </li>
        </OL>
        <P>
          Scores entered on the schedule, in live stats or on the bracket all update the same
          games. On public brackets, a stats icon on finished games with stats opens that
          game&apos;s stats in a new tab.
        </P>
      </>
    ),
  },
  {
    id: 'ai-features',
    title: '7. AI Features',
    searchText:
      'ai schedule import pdf csv excel box score scan photo stat sheet import pdf game recap gemini virtual assistant chat',
    body: (
      <>
        <H3>Schedule import (Pro)</H3>
        <P>
          <Code>/import</Code> → upload a PDF, CSV, or Excel. The AI extracts teams, dates,
          times, matchups and lets you confirm each field before saving.
        </P>
        <H3>Box-score scanning (Pro)</H3>
        <UL>
          <li>
            <B>Basketball</B>: Stats Hub → <B>New Game</B> → <B>Import Box Score</B>. Upload a
            photo or PDF of each team&apos;s paper box score, and a stats game is created from it.
          </li>
          <li>
            <B>Other sports</B>: open a match&apos;s box score → <B>Scan Box Score</B>. Take a photo
            or upload a photo or PDF of each team&apos;s sheet (or one sheet with both teams), and the
            AI fills in the player stats for you to check.
          </li>
        </UL>
        <H3>Game recaps</H3>
        <P>
          On a finished game&apos;s summary or <B>Game Stats</B> page → <B>Generate Recap</B>.
        </P>
        <H3>Virtual Assistant</H3>
        <P>
          The chat bubble on every page answers questions about using Schedule Guru. It
          doesn&apos;t read your league data.
        </P>
      </>
    ),
  },
  {
    id: 'communication',
    title: '8. Communication',
    searchText:
      'notify broadcast email collaborators team contacts mailgun collaborator invite roster reminder renewal reminder receipt support ticket',
    body: (
      <>
        <H3>Notify (Pro)</H3>
        <P>
          From a schedule → <B>Notify</B> → write a message and choose who gets it:{' '}
          <B>Collaborators</B> (on by default) and/or <B>Team Contacts</B>. Sent by email.
        </P>
        <H3>Automatic emails</H3>
        <UL>
          <li>Verification and password reset emails.</li>
          <li>Collaborator invites — sent when you add a co-organizer.</li>
          <li>Team contact invites — sent from the team list.</li>
          <li>Roster reminders — the day before the event starts, if a roster hasn&apos;t been submitted.</li>
          <li>Payment receipts, and a reminder about 5 days before your plan renews or expires.</li>
          <li>Officials emails (with the Officials add-on): assignments, changes and reminders.</li>
        </UL>
        <H3>Support</H3>
        <P>
          User menu → <B>Support</B> opens a ticket, and our replies show up in the same thread.
        </P>
      </>
    ),
  },
  {
    id: 'subscription',
    title: '9. Subscription (Pro)',
    searchText:
      'pricing free pro monthly annual officials bundle card visa mastercard paypal billing auto-renew receipts switch to annual coupon cancel renewal expiry',
    body: (
      <>
        <H3>Free</H3>
        <UL>
          <li>Round robin, up to 8 teams (other formats with a free account, also up to 8 teams)</li>
          <li>Basic PDF / Excel / iCal exports</li>
          <li>Public share link</li>
          <li>Score entry</li>
          <li>Bracket Library templates and share links</li>
        </UL>
        <H3>Pro</H3>
        <UL>
          <li>Unlimited teams and tournaments, pools and every format</li>
          <li>Live basketball stat tracker, and player box scores for every other sport</li>
          <li>AI schedule import, AI box-score scanning and AI game recaps</li>
          <li>Standings, tie-breakers, leaderboards, awards and Player of the Week graphics</li>
          <li>Roster manager with photos + CSV/PDF export, team contacts and roster lock</li>
          <li>Collaborators and email broadcasts (Notify)</li>
          <li>Branded exports, promo images and printable brackets</li>
          <li>Bracket Library extras: no watermark, scores, team lists, Manage as Tournament</li>
          <li>Priority support</li>
        </UL>
        <H3>Pricing (USD)</H3>
        <UL>
          <li>
            <B>Pro Monthly</B>: $24 USD/month
          </li>
          <li>
            <B>Pro Annual</B>: $199 USD/year ($16.58 USD/month equivalent, save 31%)
          </li>
          <li>
            <B>Officials Scheduling add-on</B>: $299 USD/year (works with Free or Pro)
          </li>
          <li>
            <B>Pro + Officials bundles</B> (card only): Annual $498 USD/year, or Flexible $323 USD
            today, then $24 USD/month + $299 USD/year
          </li>
        </UL>
        <P>
          Pay by credit or debit card (Visa or Mastercard) on the secure RBC Plug&apos;n Pay page,
          or with PayPal. Both renew automatically. Subscribe at <Code>/pricing</Code>.
        </P>
        <H3>Managing your plan</H3>
        <P>
          User menu → <B>Billing</B>:
        </P>
        <UL>
          <li>See your plan and receipts.</li>
          <li>Turn auto-renew off or back on.</li>
          <li>Update your card.</li>
          <li>Pay a failed renewal.</li>
          <li>Pro Monthly card subscribers can <B>Switch to annual</B>.</li>
        </UL>
        <P>
          PayPal plans are cancelled in PayPal (<B>Manage in PayPal</B>). Have a coupon code?
          Enter it at checkout.
        </P>
      </>
    ),
  },
  {
    id: 'sharing',
    title: '10. Public Sharing',
    searchText: 'public share link schedule calendar standings leaderboard season stats teams rosters bracket box scores player pages hidden visible header colors theme branding accent',
    body: (
      <>
        <P>Every schedule has a public share link. The public page has tabs for:</P>
        <UL>
          <li>The schedule and calendar</li>
          <li>Standings</li>
          <li>Leaderboard and season stats</li>
          <li>Teams, with rosters</li>
          <li>The bracket (elimination events)</li>
        </UL>
        <P>
          Visitors can open box scores and player pages. Flip the schedule to <B>Hidden</B> to
          turn off every public page instantly.
        </P>
        <P>
          Give each league its own look: in the <B>Share</B> window, pick the two{' '}
          <B>Header colors</B> (the header background and an accent for the league name, icons and
          stripe). Choose a preset or any color, check the preview, then <B>Save colors</B>.{' '}
          <B>Reset</B> goes back to navy and amber.
        </P>
        <P>
          The header color also fills the public table headers and the Game Summary score header and
          team bars. The accent fills the selected tab and the Share Recap button, and the Share Recap
          graphic uses your header color as its background with accent highlights.
        </P>
      </>
    ),
  },
  {
    id: 'troubleshooting',
    title: '11. Troubleshooting',
    searchText:
      'troubleshooting regenerate did nothing cannot see pro features roster not saving collaborator email didn\'t arrive spam support',
    body: (
      <>
        <P>
          <B>&quot;Regenerate did nothing&quot;</B> — Click Regenerate again. Every click produces a
          different arrangement. If a match keeps re-pairing, lock it with the padlock icon.
        </P>
        <P>
          <B>&quot;I can&apos;t see Pro features&quot;</B> — Check user menu → <B>Billing</B> to confirm
          your plan is active. If you just subscribed, log out and back in.
        </P>
        <P>
          <B>&quot;A player disappeared from my roster&quot;</B> — Rows without a name aren&apos;t saved.
          Give every player a name, then save again.
        </P>
        <P>
          <B>&quot;Can&apos;t invite a collaborator&quot;</B> — Collaborators need Pro, and only the
          schedule&apos;s owner can invite. If the person has no account yet, they get an email to
          create a free one.
        </P>
        <P>
          <B>&quot;Email didn&apos;t arrive&quot;</B> — Check spam and promotions folders, and make sure
          the address is spelled correctly.
        </P>
        <P>
          Still stuck? Open a ticket from <B>Support</B> or email{' '}
          <a href="mailto:support@scheduleguru.ws" className="text-[#00247D] font-semibold hover:underline">
            support@scheduleguru.ws
          </a>
          .
        </P>
      </>
    ),
  },
  {
    id: 'officials',
    title: '12. Officials Scheduling (add-on)',
    searchText:
      'officials referees umpires scorekeepers add-on 299 assign availability portal accept decline payments rates templates batches officials manual',
    body: (
      <>
        <P>
          The <B>Officials Scheduling</B> add-on ($299 USD/year, works with any plan) lets you build an officials list, collect
          availability, assign crews to your existing games with conflict warnings, send accept/decline emails and reminders,
          and track what every official is owed and paid. Officials join free and use the <B>Officials Portal</B>.
        </P>
        <P>
          Everything is covered step by step in the{' '}
          <Link to="/manual/officials" className="text-[#00247D] font-semibold hover:underline" data-testid="manual-officials-link">
            Officials manual
          </Link>{' '}
          (sign-in required), with one part for organizers and schedulers and one for officials.
        </P>
      </>
    ),
  },
];
